import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { StaffProfile } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';

import { useUpdateProfile } from '../hooks/use-account-mutations';
import {
  fromProfile,
  profileSchema,
  toProfileUpdate,
  type ProfileValues,
} from '../schemas/account-schemas';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['name', 'locale']);

/** Name and preferred language, shown to teammates and used for emails Nucleus sends you. */
export function ProfileCard({ me }: { me: StaffProfile }) {
  const { t } = useTranslation(['account', 'common']);
  const message = useErrorMessage();
  const update = useUpdateProfile();
  const form = useForm<ProfileValues>({
    resolver: useI18nResolver(profileSchema),
    defaultValues: fromProfile(me),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      const saved = await update.mutateAsync(toProfileUpdate(values));
      form.reset(fromProfile(saved));
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <FormSection
      title={t('account:profile.title')}
      description={t('account:profile.description')}
      id={SECTION_ANCHORS.accountProfile}
      tour="account.profile"
    >
      <Form {...form}>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-4"
          aria-label={t('account:profile.title')}
        >
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('account:profile.name')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="name" maxLength={120} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="locale"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('account:profile.locale')}</FormLabel>
                  <FormControl>
                    <Input placeholder="en-GB" className="font-mono text-[13px]" {...field} />
                  </FormControl>
                  <FormDescription>{t('account:profile.localeHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={update.isPending || !form.formState.isDirty}>
              {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
            </Button>
          </div>
        </form>
      </Form>
    </FormSection>
  );
}
